import { EFinancingProgram, EPaymentFrequency } from '@if/core/types'
import { FormContainer, FormSetup, optional, PageContainer, useIfUser } from '@if/mui'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { useCalculatorEstimate, useFirstPaymentOptions } from '@src/data/api/calculator-api/calculator-api'
import {
  useMerchantById,
  useMerchantByParentId,
  useMerchantByParentIdQuery,
} from '@src/data/api/merchants-api/merchants-api'
import { CalculatorEstimateRequestDto } from '@src/types/CalculatorEstimateDto'
import { CreditRatingOption } from '@src/types/Constants'
import { Merchant } from '@src/types/Merchant'
import { PersonalLoanCalculatorSchema, PersonalLoanCalculatorSchemaType } from '@src/types/PersonalLoanCalculatorSchema'
import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CalculatorForm } from './PersonalLoanCalculator/CalculatorForm'
import { CalculatorSkeleton } from './PersonalLoanCalculator/components/CalculatorSkeleton'
import {
  buildCreditRatingOptions,
  createEstimateRequest,
  getDeliveryAndFirstPaymentDates,
} from './PersonalLoanCalculator/personalLoanCalculator-utils'

// Module-scope so the reference is stable across renders (captures nothing).
// The amount is left empty on purpose: a pre-filled one reads as a ready form while the
// estimate is still waiting on a payment plan, so nothing appears to happen.
const useFormSetup = (): FormSetup<typeof PersonalLoanCalculatorSchema> => ({
  schema: PersonalLoanCalculatorSchema,
  defaultValues: {},
})

// A banner user's subsidiaries populate the merchant picker; loaded as a page
// resource so the shell shows a skeleton until they arrive. The connected user's
// own merchant is already loaded at boot by MerchantProvider (read via
// useMerchant), so it isn't fetched here. Non-banner users have no subsidiaries,
// so the query stays disabled (optional → absent) and the page renders immediately.
const usePersonalCalculatorResources = () => {
  const user = useIfUser()
  return {
    subsidiaries: optional(useMerchantByParentIdQuery(user.isBannerUser ? user.merchantId : '')),
  }
}

// The calculator body, mounted once the primary resources are loaded. Reads the
// (now cached) merchant queries and owns the on-demand estimate.
const PersonalLoanCalculatorContent = () => {
  const user = useIfUser()
  const connectedMerchant = useMerchant()

  const isBanner = user.isBannerUser
  const [subsidiaries] = useMerchantByParentId(isBanner ? user.merchantId : '')
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | undefined>(undefined)

  // Banner users calculate for a chosen subsidiary (loaded on selection); everyone
  // else uses their own merchant, already loaded (and non-null) via MerchantProvider.
  const [selectedMerchantData, isLoadingSelected] = useMerchantById(isBanner ? (selectedMerchant?.id ?? '') : '')
  const merchant = isBanner ? selectedMerchantData : connectedMerchant
  const effectiveMerchantId = isBanner ? (selectedMerchant?.id ?? '') : connectedMerchant.id
  const isLoadingMerchant = isBanner ? isLoadingSelected : false

  // `onCalculate` snapshots the form into the request DTO; the filters form re-runs it on change.
  const [estimateData, setEstimateData] = useState<CalculatorEstimateRequestDto | null>(null)
  const [calculatedRate, isFetching] = useCalculatorEstimate(EFinancingProgram.Personal, estimateData)

  const creditRatingOptions = useMemo<CreditRatingOption[]>(() => buildCreditRatingOptions(), [])

  // The program's own first-payment rules, so a quote uses a date a worksheet would accept.
  const { deliveryOn } = getDeliveryAndFirstPaymentDates()
  const firstPaymentDates = useFirstPaymentOptions(EFinancingProgram.Personal, deliveryOn, EPaymentFrequency.Monthly)
  const firstPaymentOn = firstPaymentDates[0]

  const onCalculate = useCallback(
    (values: PersonalLoanCalculatorSchemaType) => {
      // Gated on `firstPaymentOn` (not `term`): quoting before the dates land would price a
      // term the program may not allow, then silently re-price.
      if (values.amountRequested && values.paymentPlanId && effectiveMerchantId && merchant && firstPaymentOn) {
        setEstimateData(
          createEstimateRequest(
            effectiveMerchantId,
            merchant.province,
            values.amountRequested,
            values.paymentPlanId,
            values.term ?? null,
            values.creditRatingMinErs,
            values.protectionPlan,
            firstPaymentOn,
          ),
        )
      }
    },
    [effectiveMerchantId, merchant, firstPaymentOn],
  )

  return (
    <FormContainer mode="filters" useFormSetup={useFormSetup} onSubmit={onCalculate} hideActions>
      <CalculatorForm
        merchant={merchant}
        effectiveMerchantId={effectiveMerchantId}
        isLoadingMerchant={isLoadingMerchant}
        isBanner={isBanner}
        subsidiaries={subsidiaries}
        selectedMerchant={selectedMerchant}
        onSelectMerchant={setSelectedMerchant}
        creditRatingOptions={creditRatingOptions}
        calculatedRate={calculatedRate}
        isFetching={isFetching}
        firstPaymentOn={firstPaymentOn}
      />
    </FormContainer>
  )
}

const PersonalLoanCalculator = () => {
  const { t } = useTranslation()
  return (
    <PageContainer
      usePrimary={usePersonalCalculatorResources}
      title={t('calculator.personalCalculatorTitle')}
      subtitle={t('calculator.personalCalculatorSubtitle')}
      loadingContent={<CalculatorSkeleton />}
    >
      {() => <PersonalLoanCalculatorContent />}
    </PageContainer>
  )
}

export default memo(PersonalLoanCalculator)
